import React, { Dispatch, SetStateAction, useCallback } from "react"
import EditHalfScreen from "../../Ui/EditHalfScreen/EditHalfScreen"
import EditSidebarHeader from "../../Ui/EditSidebar/EditSidebarHeader"
import {
  CustomOperation,
  CustomReasonCode,
  CustomState,
  CustomStateType,
  Maybe,
  OrderCustomState,
  OrderOperationSearchHit,
  OrderOperationSearchHits,
  OrderReleaseSearchHit
} from "lib/types/generated/graphql-types"
import DeliveryAction from "./DeliveryAction"
import CompensationAction from "./CompensationAction"
import { ApolloQueryResult, useQuery } from "@apollo/client"
import GET_ORDER from "../../../graphql/queries/order/GetOrder"
import ALL_REASON_CODES from "../../../graphql/queries/settings/reasonCodes/AllReasonCodes"
import ALL_CUSTOM_STATES from "../../../graphql/queries/settings/customState/AllCustomStates"
import RefundAction from "./RefundAction"
import CancellationAction from "./CancellationAction"
import ReleaseAction from "./ReleaseAction"
import OrderLinesModificationAction from "./OrderLinesModificationAction"
import ShippingFeesModificationAction from "./ShippingFeesModificationAction"
import AddressModificationAction from "./AddressModificationAction"

type Props = {
  show: boolean
  setShow: Dispatch<SetStateAction<boolean>>
  operation: OrderOperationSearchHit
  refetchOperations: () => Promise<ApolloQueryResult<OrderOperationSearchHits>>
  setCreatedOperations: Dispatch<SetStateAction<OrderOperationSearchHit[]>>
  setFailedOperations: Dispatch<SetStateAction<OrderOperationSearchHit[]>>
  getOperationConfig: (operation: string) =>
    | {
        icon: React.JSX.Element
        color: string
        label: string
      }
    | undefined
}

const OperationActions = ({
  setShow,
  operation,
  refetchOperations,
  setCreatedOperations,
  setFailedOperations,
  getOperationConfig
}: Props) => {
  const {
    data: orderData,
    loading: orderLoading,
    refetch
  } = useQuery(GET_ORDER, {
    variables: { id: operation.orderId },
    fetchPolicy: "no-cache"
  })
  const { data: reasonData, loading: reasonLoading } = useQuery(ALL_REASON_CODES)
  const customReasons = reasonData?.Order?.customReasonCodes?.codes

  const { data: customStateData } = useQuery(ALL_CUSTOM_STATES)
  const allCustomStates: CustomState[] = customStateData?.Order?.customStates?.states ?? []

  const getCustomState = useCallback(
    (stateId: string) => allCustomStates.find((s) => s.id === stateId),
    [allCustomStates]
  )

  const getCustomCode = (code: Maybe<string> | undefined) =>
    customReasons?.find((customCode: CustomReasonCode) => customCode.code === code)

  const operationTypeToCustomOperation: Record<string, CustomOperation> = {
    Delivery: CustomOperation.Delivery,
    Compensation: CustomOperation.Compensation,
    Refund: CustomOperation.Refund,
    Release: CustomOperation.Release,
    Cancellation: CustomOperation.Cancellation,
    OrderLinesModification: CustomOperation.ModifyOrderLines,
    ShippingFeesModification: CustomOperation.ModifyShippingFees,
    AddressesModification: CustomOperation.ModifyOrderAddresses
  }

  const order = orderData?.Order?.details
  const customStates: OrderCustomState[] = order?.status?.customStates ?? []
  const customOperation = operationTypeToCustomOperation[operation.type]
  const blockingStateNames = customOperation
    ? customStates
        .filter(
          (s: OrderCustomState) =>
            s.type === CustomStateType.Blocking && s.operations?.includes(customOperation)
        )
        .map((s: OrderCustomState) => getCustomState(s.id)?.description ?? s.id)
    : []
  const isOperationBlocked = blockingStateNames.length > 0
  const blockedReason = isOperationBlocked
    ? `Blocked by custom state: ${blockingStateNames.join(", ")}`
    : ""

  const getOperation = () => {
    switch (operation.type) {
      case "Delivery":
        return (
          <DeliveryAction
            operation={operation}
            order={orderData?.Order?.details}
            orderHistory={orderData?.Order?.history}
            getCustomCode={getCustomCode}
            setShow={setShow}
            refetchOperations={refetchOperations}
            setCreatedOperations={setCreatedOperations}
            setFailedOperations={setFailedOperations}
            isOperationBlocked={isOperationBlocked}
            blockedReason={blockedReason}
          />
        )
      case "Compensation":
        return (
          <CompensationAction
            operation={operation}
            order={orderData?.Order?.details}
            orderHistory={orderData?.Order?.history}
            getCustomCode={getCustomCode}
            setShow={setShow}
            setCreatedOperations={setCreatedOperations}
            setFailedOperations={setFailedOperations}
            isOperationBlocked={isOperationBlocked}
            blockedReason={blockedReason}
          />
        )
      case "Refund":
        return (
          <RefundAction
            operation={operation}
            order={orderData?.Order?.details}
            orderHistory={orderData?.Order?.history}
            getCustomCode={getCustomCode}
            setShow={setShow}
            refetchOrder={refetch}
            setCreatedOperations={setCreatedOperations}
            setFailedOperations={setFailedOperations}
            isOperationBlocked={isOperationBlocked}
            blockedReason={blockedReason}
          />
        )
      case "Release":
        return (
          <ReleaseAction
            operation={operation as OrderReleaseSearchHit}
            order={orderData?.Order?.details}
            orderHistory={orderData?.Order?.history}
            getCustomCode={getCustomCode}
            setShow={setShow}
            refetchOrder={refetch}
            setCreatedOperations={setCreatedOperations}
            setFailedOperations={setFailedOperations}
            isOperationBlocked={isOperationBlocked}
            blockedReason={blockedReason}
          />
        )
      case "Cancellation":
        return (
          <CancellationAction
            operation={operation}
            order={orderData?.Order?.details}
            orderHistory={orderData?.Order?.history}
            getCustomCode={getCustomCode}
            setShow={setShow}
            setCreatedOperations={setCreatedOperations}
            setFailedOperations={setFailedOperations}
            isOperationBlocked={isOperationBlocked}
            blockedReason={blockedReason}
          />
        )
      case "OrderLinesModification":
        return (
          <OrderLinesModificationAction
            operation={operation}
            order={orderData?.Order?.details}
            orderHistory={orderData?.Order?.history}
            getCustomCode={getCustomCode}
            setShow={setShow}
            refetchOperations={refetchOperations}
            setCreatedOperations={setCreatedOperations}
            setFailedOperations={setFailedOperations}
            isOperationBlocked={isOperationBlocked}
            blockedReason={blockedReason}
          />
        )
      case "ShippingFeesModification":
        return (
          <ShippingFeesModificationAction
            operation={operation}
            order={orderData?.Order?.details}
            orderHistory={orderData?.Order?.history}
            getCustomCode={getCustomCode}
            setShow={setShow}
            refetchOperations={refetchOperations}
            setCreatedOperations={setCreatedOperations}
            setFailedOperations={setFailedOperations}
            isOperationBlocked={isOperationBlocked}
            blockedReason={blockedReason}
          />
        )
      case "AddressesModification":
        return (
          <AddressModificationAction
            operation={operation}
            order={orderData?.Order?.details}
            orderHistory={orderData?.Order?.history}
            getCustomCode={getCustomCode}
            setShow={setShow}
            setCreatedOperations={setCreatedOperations}
            isOperationBlocked={isOperationBlocked}
            blockedReason={blockedReason}
          />
        )
    }
  }

  if (orderLoading || reasonLoading) {
    return (
      <EditHalfScreen loading={orderLoading || reasonLoading} cancelEvent={() => setShow(false)}>
        <EditSidebarHeader title={`Handle ${getOperationConfig(operation.type)?.label}`} cancelEvent={() => setShow(false)} />
      </EditHalfScreen>
    )
  }

  return (
    <>
      <EditHalfScreen cancelEvent={() => setShow(false)}>
        <EditSidebarHeader
          title={`Handle ${getOperationConfig(operation.type)?.label}`}
          icon={getOperationConfig(operation.type)?.icon}
          id={operation.id}
          cancelEvent={() => setShow(false)}
          width={"100%"}
        />
        {getOperation()}
      </EditHalfScreen>
    </>
  )
}

export default OperationActions
